import DSAuth from '../DSAuth';
import { AuthChoice } from '../data/AuthChoice';
import { GenericRequest, HTTPFetchResponse } from '../data/AuthTypes';
import { stringConstants, headerConstants } from '../../AuthConstants';
import { SentryLogger } from '../../../sentryLogging/SentryLogger';
import { Severity } from '@sentry/browser';
import {
	Authenticator,
	AuthenticateError,
	logEvent,
	analyticsConstants,
} from './Authenticator';
import { Logger } from '../../Logger';
import { AotCategory, logConstants } from '../../../utils/Constants';
import { PrimaryTokenFetchFailure } from '../../AuthErrors';
import { HttpStatusCodes } from '../../../utils/http/HttpConstants';

export default class CertificateAuthenticator implements Authenticator {
	async getToken(
		authProtocol: AuthChoice,
		authProtocolsRequest: GenericRequest,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<HTTPFetchResponse | AuthenticateError> {
		if (!authProtocol.getLocation().startsWith('https:')) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Skipping Smart Card authentication as URL is not HTTPS`,
				AotCategory.Info
			);

			return AuthenticateError.UnsuitableProtocol;
		}

		SentryLogger.getInstance().addBreadCrumb({
			category: 'CertificateAuthenticator',
			level: Severity.Info,
			message: 'getToken for CertificateAuthenticator',
		});

		return await this.fetchPrimaryToken(
			authProtocolsRequest.body,
			authProtocol.getLocation(),
			transactionId,
			referenceUri,
			id
		);
	}

	private async fetchPrimaryToken(
		body: string,
		protocolUrl: string,
		transactionId: string,
		referenceUri: string,
		id: string
	): Promise<HTTPFetchResponse | AuthenticateError> {
		const headers = DSAuth.getInstance().addBasicHeaders(
			{
				Accept: headerConstants.TOKEN_RESPONSE_CONTENT_TYPE,
				[headerConstants.CONTENT_TYPE]: headerConstants.TOKEN_REQUEST_CONTENT_TYPE,
			},
			transactionId
		);

		let response;
		try {
			response = await DSAuth.getInstance().fetch(
				stringConstants.POST,
				protocolUrl,
				headers,
				body,
				referenceUri,
				true,
				'',
				true
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: PRIMARY_TOKEN_REQUEST_FAILURE: ${e.message}`
			);
			throw new PrimaryTokenFetchFailure(e);
		}

		if (response.statusCode === HttpStatusCodes.HTTP_STATUS_OK) {
			logEvent(analyticsConstants.LABEL_CLIENTCERTIFICATEAUTHIMPL, 1);
			return response;
		}

		if (response.statusCode === HttpStatusCodes.HTTP_STATUS_FORBIDDEN) {
			Logger.getInstance().writeAot(
				AotCategory.Error,
				`Failed to fetch primary token - Forbidden response received`
			);
			// TODO: Remove after smart card implementation
			return AuthenticateError.UnsuitableProtocol;
		}

		const error = `Failed to fetch primary token - unexpected HTTP response status code: ${response.statusCode}`;
		Logger.getInstance().write(
			logConstants.ERROR,
			`${id}: ${error}`,
			AotCategory.Error,
			error
		);
		throw new PrimaryTokenFetchFailure(new Error(error));
	}
}
